'use server'

import { apiRequest, ApiResponse } from '../request'

export type SLAType =
    | 'FIRST_RESPONSE'
    | 'STAGE_TIME'
    | 'NO_ACTIVITY'
    | 'TASK_OVERDUE'

/** Regra de SLA: só mede. Quem age na violação é o fluxo com o gatilho "SLA violado". */
export interface SalesSLA {
    id: number
    organizationId: number
    name: string
    description?: string
    type: SLAType
    targetMinutes: number
    pipelineId?: number
    pipeline?: {
        id: number
        name: string
    }
    stageId?: number
    stage?: {
        id: number
        name: string
    }
    active: boolean
    createdAt: string
    updatedAt: string
    _count?: {
        breaches: number
    }
}

export interface SLABreach {
    id: number
    slaId: number
    sla?: SalesSLA
    organizationId: number
    agendaEventId?: number | null
    dealId?: number
    deal?: {
        id: number
        title: string
    }
    leadId?: number
    lead?: {
        id: number
        name: string
    }
    breachedAt: string
    /** Preenchido sozinho quando a condição deixa de valer, ou à mão. */
    resolvedAt?: string | null
    actionTaken?: string | null
    createdAt: string
}

export interface CreateSLAData {
    name: string
    description?: string
    type: SLAType
    targetMinutes: number
    pipelineId?: number
    stageId?: number
}

export interface UpdateSLAData {
    name?: string
    description?: string
    type?: SLAType
    targetMinutes?: number
    pipelineId?: number
    stageId?: number
    active?: boolean
}

export interface SLABreachFilters {
    slaId?: number
    resolved?: boolean
    startDate?: string
    endDate?: string
}

// SLAs

export async function getSLAs(): Promise<ApiResponse<SalesSLA[]>> {
    return apiRequest<SalesSLA[]>('sales/slas', {})
}

export async function getSLA(id: number): Promise<ApiResponse<SalesSLA>> {
    return apiRequest<SalesSLA>(`sales/slas/${id}`, {})
}

export async function createSLA(
    data: CreateSLAData
): Promise<ApiResponse<SalesSLA>> {
    return apiRequest<SalesSLA>('sales/slas', { data }, 'post')
}

export async function updateSLA(
    id: number,
    data: UpdateSLAData
): Promise<ApiResponse<SalesSLA>> {
    return apiRequest<SalesSLA>(`sales/slas/${id}`, { data }, 'patch')
}

export async function deleteSLA(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`sales/slas/${id}`, {}, 'delete')
}

// Breaches

export async function getSLABreaches(
    filters?: SLABreachFilters
): Promise<ApiResponse<SLABreach[]>> {
    return apiRequest<SLABreach[]>('sales/slas/breaches', { params: filters })
}

export async function resolveSLABreach(
    id: number,
    actionTaken: string
): Promise<ApiResponse<SLABreach>> {
    return apiRequest<SLABreach>(
        `sales/slas/breaches/${id}/resolve`,
        { data: { actionTaken } },
        'patch'
    )
}

// Labels

export const SLA_TYPE_LABELS: Record<SLAType, string> = {
    FIRST_RESPONSE: 'Primeira resposta',
    STAGE_TIME: 'Tempo na etapa',
    NO_ACTIVITY: 'Sem atividade',
    TASK_OVERDUE: 'Tarefa atrasada',
}

export const SLA_TYPE_DESCRIPTIONS: Record<SLAType, string> = {
    FIRST_RESPONSE: 'Lead criado nos últimos 7 dias que ainda não recebeu nenhuma mensagem da equipe dentro do prazo.',
    STAGE_TIME: 'Oportunidade aberta parada na etapa escolhida por mais que o prazo, contado da entrada na etapa.',
    NO_ACTIVITY: 'Lead em andamento sem nada novo na linha do tempo durante o prazo.',
    TASK_OVERDUE: 'Tarefa de lead não concluída depois do prazo, contado do fim da tarefa.',
}
